import React, { ChangeEvent, Component, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import ICreateCatalogDTO from "../../../../models/DTO/ICreateCatalogDTO";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import IFishbowlGetLocationGroupsDTO from "../../../../models/DTO/IFishbowlGetLocationGroupsDTO";
import IOnCreateCatalogDTO from "../../../../models/DTO/IOnCreateCatalogDTO";
import IOnFishbowlGetLocationGroupsDTO from "../../../../models/DTO/IOnFishbowlGetLocationGroupsDTO";
import { IOnUpdateCatalogDTO } from "../../../../models/DTO/IOnUpdateCatalogDTO";
import { IUpdateCatalogDTO } from "../../../../models/DTO/IUpdateCatalogDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IFishbowlLocation from "../../../../models/Fishbowl/IFishbowlLocation";
import ICatalog from "../../../../models/ICatalog";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import OwButton from "../../../shared/OwButton/OwButton";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwS3Uploader from "../../../shared/OwS3Uploader/OwS3Uploader";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import { Socket } from "socket.io-client";
import * as _ from "lodash-es";
import IUiSocketSetup from "../../../../models/IUiSocketSetup";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import CurrenciesEnum from "../../../../models/CurrenciesEnum";
import { CurrencySelect } from "../../../shared/CurrencySelect/CurrencySelect";
import ICatalog_Item from "../../../../models/ICatalog_Item";
import IOnTypeaheadCatalogItemDTO from "../../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import Typeahead from "../../../shared/Typeahead/Typeahead";
import ITypeaheadCatalogItemDTO from "../../../../models/DTO/ITypeaheadCatalogItemDTO";
import { SocketContext } from "../../../../contexts/SocketContext";
import UiContext from "../../../../contexts/UiContext";
import OwToggle from "@components/shared/OwToggle/OwToggle";

type Props = {
    cancel: (refreshData?: boolean) => void;
    catalogModel?: ICatalog;
};

interface CatalogOptions {
    fishbowl_auto_refresh_inventory: boolean;
    fishbowl_assigned_warehouse_id: string;
    fishbowl_assigned_location_group_id: string;
    xero_default_price_group_id: string;
}

export const CatalogSidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [name, setName] = useState(props.catalogModel?.name);
    const [email, setEmail] = useState(props.catalogModel?.email);
    const [active, setActive] = useState(props.catalogModel?.active);
    const [orderPrefixOverride, setOrderPrefixOverride] = useState(props.catalogModel?.order_prefix_override);
    const [currencyId, setCurrencyId] = useState<CurrenciesEnum | undefined>(props.catalogModel?.currency_id);
    const [freeEntryCatalogItem, setFreeEntryCatalogItem] = useState<ICatalog_Item | undefined>();
    const [foundFreeEntryItems, setFoundFreeEntryItems] = useState<ICatalog_Item[]>([]);
    const [selectedFreeEntryItem, setSelectedFreeEntryItem] = useState<ICatalog_Item | undefined>();
    const [saveActive, setSaveActive] = useState(false);
    const [clearFreeEntryTypeahead, setClearFreeEntryTypeahead] = useState(false);
    const [fishbowlLocations, setFishbowlLocations] = useState<IFishbowlLocation[]>([]);
    const [catalogOptions, setCatalogOptions] = useState<CatalogOptions>({
        fishbowl_assigned_warehouse_id: props.catalogModel?.fishbowl_assigned_warehouse_id || "",
        fishbowl_assigned_location_group_id: props.catalogModel?.fishbowl_assigned_location_group_id || "",
        fishbowl_auto_refresh_inventory: props.catalogModel?.fishbowl_auto_refresh_inventory || false,
        xero_default_price_group_id: props.catalogModel?.xero_default_price_group_id?.toString() || "",
    });

    const getFishbowlLocationGroups = () => {
        const payload: IFishbowlGetLocationGroupsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.FishbowlGetLocationGroups, payload);
    };

    const onGetFishbowlLocationGroups = (data: IOnFishbowlGetLocationGroupsDTO) => {
        setFishbowlLocations(data.locations);
    };

    const onCreateCatalog = (data: IOnCreateCatalogDTO) => {
        setSaveActive(false);
        props.cancel(true);
    };

    const onUpdateCatalog = (data: IOnUpdateCatalogDTO) => {
        setActive(false);
        props.cancel(true);
    };

    const onTypeheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        setFoundFreeEntryItems(data.catalog_items);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogsOnCreateCatalog, fn: onCreateCatalog },
        { endpoint: EndpointsEnum.CatalogsOnUpdateCatalog, fn: onUpdateCatalog },
        { endpoint: EndpointsEnum.FishbowlOnGetLocationGroups, fn: onGetFishbowlLocationGroups },
        { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeheadCatalogItem },
    ];

    const setupSockets = () => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }
    };

    const tearDownSockets = () => {
        for (const _socket of sockets) {
            socket.removeListener(_socket.endpoint, _socket.fn);
        }
    };

    useEffect(() => {
        setupSockets();

        if (supplierModel.Supplier_Integration.has_fishbowl) {
            getFishbowlLocationGroups();
        }

        return () => {
            tearDownSockets();
        };
    });

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const _name = e.target.name;

        if (_name === "fishbowl_assigned_warehouse_id") {
            if (catalogOptions.fishbowl_assigned_location_group_id && catalogOptions.fishbowl_assigned_location_group_id.length) {
                for (const locationDetail of fishbowlLocations) {
                    if (locationDetail.id.toString() === value.toString()) {
                        const updatedOptions = {
                            ...catalogOptions,
                        };

                        updatedOptions.fishbowl_assigned_location_group_id = value;

                        setCatalogOptions(updatedOptions);
                    }
                }
            }
        } else {
            switch (_name) {
                case "name":
                    setName(value);
                    break;

                case "email":
                    setEmail(value);
                    break;

                case "active":
                    setActive(value);
                    break;

                case "order_prefix_override":
                    setOrderPrefixOverride(value);
                    break;

                case "currency_id":
                    setCurrencyId(value);
                    break;

                case "fishbowl_assigned_warehouse_id":
                case "fishbowl_auto_refresh_inventory":
                case "xero_default_price_group_id":
                    {
                        const updatedOptions = {
                            ...catalogOptions,
                        };

                        // @ts-expect-error
                        updatedOptions[_name] = value;
                        setCatalogOptions(updatedOptions);
                    }
                    break;
            }
        }
    };

    const save = (e: any) => {
        const payload: ICreateCatalogDTO | IUpdateCatalogDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            version: "1.0",
            name: name || "",
            active,
            email,
            order_prefix_override: orderPrefixOverride,
            currency_id: currencyId,
        };

        if (props.catalogModel) {
            // @ts-expect-error
            payload.fishbowl_assigned_warehouse_id = catalogOptions.fishbowl_assigned_warehouse_id;
            // @ts-expect-error
            payload.fishbowl_auto_refresh_inventory = catalogOptions.fishbowl_auto_refresh_inventory;
            // @ts-expect-error
            payload.fishbowl_assigned_location_group_id = catalogOptions.fishbowl_assigned_location_group_id;

            if (catalogOptions.xero_default_price_group_id.length) {
                // @ts-expect-error
                payload.xero_default_price_group_id = catalogOptions.xero_default_price_group_id;
            } else {
                // @ts-expect-error
                payload.xero_default_price_group_id = null;
            }

            if (selectedFreeEntryItem) {
                // @ts-expect-error
                payload.free_entry_catalog_item_id = selectedFreeEntryItem.id;
            }

            // @ts-expect-error
            payload.catalogGuid = props.catalogModel.guid;

            socket.emit(EndpointsEnum.CatalogsUpdateCatalog, payload);
        } else {
            socket.emit(EndpointsEnum.CatalogsCreateCatalog, payload);
        }

        setSaveActive(true);
    };

    const cancel = () => {
        props.cancel(false);
    };

    const renderLocationTitle = (row: IFishbowlLocation): string => {
        return `${row.LocationGroupName} - ${row.name}`;
    };

    const lookupFreeEntryItems = (_name: string) => {
        if (props.catalogModel) {
            const payload: ITypeaheadCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid: props.catalogModel?.guid,
                name: _name,
                options: {
                    searchName: true,
                    searchDescription: true,
                    searchSku: true,
                    searchUpc: true,
                },
            };

            socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
        }
    };

    const selectFreeEntryItem = (item: ICatalog_Item) => {
        setSelectedFreeEntryItem(item);
    };

    const clearFreeEntryTypeaheadFn = () => {
        setClearFreeEntryTypeahead(false);
        setSelectedFreeEntryItem(undefined);
    };

    return (
        <div className="CatalogSidebar" style={{ height: "100vh", overflow: "hidden" }}>
            <div className={"p-2"} style={{ height: "15vh" }}>
                <div className="text-end">
                    <button className="btn btn-danger btn-sm" onClick={cancel}>
                        <i className="far fa-times" />
                    </button>
                </div>
                <OwTitle leftIconName={"far fa-book"} title={props.catalogModel ? "update_catalog" : "create_catalog"} />
                <hr />
            </div>
            <div className="CatalogItemForm p-2" style={{ height: "85vh", overflowY: "scroll" }}>
                <div>
                    <OwLabel title={"name"} />
                    <div>
                        <OwInputWrapper fn={handleChange} value={name} name={"name"} />
                    </div>
                </div>

                <div className={"mt-3"}>
                    <OwLabel title={"order_prefix_override"} />
                    <div>
                        <OwInputWrapper fn={handleChange} value={orderPrefixOverride} name={"order_prefix_override"} />
                    </div>
                </div>

                {supplierModel.allow_free_entry_items && props.catalogModel ? (
                    <div className="mt-3">
                        <OwLabel title={"free_entry_item"} />
                        <div>
                            <Typeahead
                                isSelectableFn={() => true}
                                typeaheadApiFn={lookupFreeEntryItems}
                                typeaheadItems={foundFreeEntryItems}
                                titleProp={"name"}
                                selectedItemFn={selectFreeEntryItem}
                                selectedItem={selectedFreeEntryItem}
                                selectedItemTitleProp={"name"}
                                scrollableList={true}
                                clearTypeaheadCallback={clearFreeEntryTypeaheadFn}
                                clearTypeahead={clearFreeEntryTypeahead}
                            />
                        </div>
                    </div>
                ) : null}

                {supplierModel.has_currencies ? <CurrencySelect updateCurrencyId={handleChange} selectedCurrencyId={currencyId} /> : null}

                {supplierModel.has_catalog_email ? (
                    <div className={"mt-3"}>
                        <OwLabel title={"email"} />
                        <div>
                            <OwInputWrapper fn={handleChange} value={email} name={"email"} />
                        </div>
                    </div>
                ) : null}

                {supplierModel.Supplier_Integration.has_fishbowl && supplierModel.Supplier_Integration.fishbowl_auto_refresh_inventory ? (
                    <div className={"mt-3 mb-3"}>
                        <div>
                            <OwCheckbox
                                name={"fishbowl_auto_refresh_inventory"}
                                checked={catalogOptions.fishbowl_auto_refresh_inventory}
                                fn={handleChange}
                                label={"fishbowl_auto_refresh_inventory"}
                            />
                        </div>
                        <div className={"mt-3"}>
                            <OwLabel title={"fishbowl_assigned_warehouse"} />
                            <OwSelectOption
                                titleProp={"name"}
                                titleRenderFn={renderLocationTitle}
                                valueProp={"id"}
                                fn={handleChange}
                                name={"fishbowl_assigned_warehouse_id"}
                                options={fishbowlLocations}
                                selectedOption={catalogOptions}
                                selectedOptionProp={"fishbowl_assigned_warehouse_id"}
                            />
                        </div>
                    </div>
                ) : null}

                {supplierModel.Supplier_Integration.has_xero ? (
                    <div className={"mt-3"}>
                        <div>
                            <OwLabel title={"xero_default_price_group"} />
                        </div>
                        <OwSelectOption
                            name={"xero_default_price_group_id"}
                            fn={handleChange}
                            selectedOption={props.catalogModel}
                            selectedOptionProp={"xero_default_price_group_id"}
                            options={props.catalogModel?.Price_Groups || []}
                            titleProp={"value"}
                            valueProp={"id"}
                        />
                    </div>
                ) : null}

                <div className={"mt-3"}>
                    <div>
                        <OwToggle fn={handleChange} name={"active"} active={!!active} label={"active"} labelOnRight={true} />
                    </div>
                </div>

                <div className={"mt-2"}>
                    <OwButton fn={save} title={"save"} active={saveActive} disabled={saveActive} />
                </div>
            </div>
        </div>
    );
};

export default CatalogSidebar;
